// src/components/ui/ImagenEvidencia.js
// Miniatura de imagen "de evidencia" (comprobantes, bitácora, asistencia, etc.)
// con manejo del caso VENCIDA: el lifecycle del bucket borra los archivos viejos
// (bitácora 30d, asistencia 60d, comprobantes/solicitudes 150d). Cuando el archivo
// ya no existe, en vez de imagen rota + 403 al abrir, muestra un placeholder con
// contexto. Al clickear (si existe) abre el <ImagenLightbox> global (no pestaña nueva).
// "Cambiar uno = cambiar todos." Ver ESTETICA_GUIA.md §0.15.
//
// 🔴 Desde el 28/10/2026 un error al cargar NO es "vencida" de entrada (caso Producción: fotos del día anterior
// salían "Vencida" y aparecían recargando 6 veces). La imagen se reintenta sola 2 veces y, si sigue sin cargar, se
// pregunta si el archivo existe: borrado → "Vencida" (como siempre); no borrado o sin respuesta → "No cargó" y se
// toca para reintentar. Detalle: src/utils/imagenReintento.js.
//
// Uso:
//   <ImagenEvidencia url={ev.url} nombre="Incidencia — Evidencia 1" size={80} radius={6}
//                    mensajeVencida="...30 días..." />
//   // o galería grande:
//   <ImagenEvidencia url={url} nombre="Pedido X" width="100%" height={250} />
import React, { useState } from 'react';
import ImagenLightbox from './ImagenLightbox';
import { ClockIcon, ArrowPathIcon, SpinnerIcon } from './Icons';
import useReintentoImagenes from '../../hooks/useReintentoImagenes';

const MENSAJE_NO_CARGO = 'La imagen no terminó de cargar (puede ser la conexión). Tocá para reintentar; si sigue igual, avisalo.';

function ImagenEvidencia({ url, nombre, size = 80, width, height, radius = 8, style, meses = 5, mensajeVencida }) {
  const [ampliada, setAmpliada] = useState(false);
  const { srcDe, estadoDe, alError, reintentar } = useReintentoImagenes();
  const estado = estadoDe(url);
  const src = srcDe(url);

  const w = width != null ? width : size;
  const h = height != null ? height : size;
  const alturaNum = typeof h === 'number' ? h : 250;
  const grande = alturaNum >= 150; // con espacio para texto completo

  const msg = mensajeVencida
    || `Esta imagen tenía más de ${meses} meses de antigüedad y el sistema la eliminó automáticamente por la política de retención del almacenamiento.`;

  const baseBox = { width: w, height: h, borderRadius: radius, flexShrink: 0, boxSizing: 'border-box', ...style };

  // Archivo vencido/inexistente → placeholder con contexto (tooltip = mensaje completo).
  if (estado === 'vencida') {
    return (
      <div
        title={msg}
        style={{
          ...baseBox,
          background: 'var(--color-fondo)',
          border: '1px solid var(--color-borde)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: grande ? '8px' : '3px', textAlign: 'center', padding: grande ? '16px' : '4px'
        }}
      >
        <span style={{ color: 'var(--color-advertencia-texto)', display: 'inline-flex' }}>
          <ClockIcon className={grande ? 'w-8 h-8' : 'w-5 h-5'} />
        </span>
        {grande ? (
          <>
            <strong style={{ fontSize: '13px', color: 'var(--color-texto)' }}>Evidencia no disponible</strong>
            <span style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', lineHeight: 1.45 }}>{msg}</span>
          </>
        ) : (
          <span style={{ fontSize: '9px', color: 'var(--color-texto-secundario)', lineHeight: 1.1 }}>Vencida</span>
        )}
      </div>
    );
  }

  // El archivo existe (o no se pudo saber) pero no cargó → se toca para reintentar
  if (estado === 'fallo') {
    const alReintentar = (e) => { e.stopPropagation(); reintentar(url); };
    return (
      <div
        role="button"
        tabIndex={0}
        title={MENSAJE_NO_CARGO}
        onClick={alReintentar}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alReintentar(e); } }}
        className="hover-bright"
        style={{
          ...baseBox,
          background: 'var(--color-fondo)',
          border: '1px solid var(--color-borde)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: grande ? '8px' : '3px', textAlign: 'center', padding: grande ? '16px' : '4px', cursor: 'pointer'
        }}
      >
        <span style={{ color: 'var(--color-primario-texto)', display: 'inline-flex' }}>
          <ArrowPathIcon className={grande ? 'w-8 h-8' : 'w-5 h-5'} />
        </span>
        {grande ? (
          <>
            <strong style={{ fontSize: '13px', color: 'var(--color-texto)' }}>No se pudo cargar</strong>
            <span style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', lineHeight: 1.45 }}>{MENSAJE_NO_CARGO}</span>
          </>
        ) : (
          <span style={{ fontSize: '9px', color: 'var(--color-texto-secundario)', lineHeight: 1.1 }}>Reintentar</span>
        )}
      </div>
    );
  }

  // Entre un reintento y otro (o mientras se comprueba si el archivo existe)
  if (estado === 'esperando') {
    return (
      <div
        title="Cargando la imagen otra vez…"
        style={{
          ...baseBox,
          background: 'var(--color-fondo)',
          border: '1px solid var(--color-borde)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--color-texto-secundario)'
        }}
      >
        <SpinnerIcon className={grande ? 'w-8 h-8 animate-spin' : 'w-5 h-5 animate-spin'} />
      </div>
    );
  }

  return (
    <>
      <img
        key={src}
        src={src}
        alt={nombre || 'Evidencia'}
        title="Ver más grande"
        onError={() => alError(url)}
        onClick={() => setAmpliada(true)}
        style={{ ...baseBox, objectFit: 'cover', cursor: 'zoom-in' }}
      />
      {ampliada && <ImagenLightbox src={src} nombre={nombre} onClose={() => setAmpliada(false)} />}
    </>
  );
}

export default ImagenEvidencia;
